import { UnitTypes } from "@/constants/bodyMetrics";
import i18n from "@/localization/i18next";

const POUNDS_IN_KG = 2.20462;

export const MIN_WEIGHT_KG = 30;
export const MAX_WEIGHT_KG = 300;
export const MAX_WEIGHT_LBS = 660;
export const MIN_WEIGHT_LBS = 66;

export const kgToLbs = (kg: number) => {
    return kg * POUNDS_IN_KG;
};

export const lbsToKg = (lbs: number) => {
    return lbs / POUNDS_IN_KG;
};

export const convertToKg = (value: number, units: UnitTypes): number => {
    if (units === UnitTypes.Metric) {
        return value;
    }
    return lbsToKg(value);
};

export const convertToLbs = (value: number, units: UnitTypes, precision?: number): number => {
    let target = value;
    if (units === UnitTypes.Imperial) {
        target = kgToLbs(value);
    }
    if (precision !== undefined) {
        return +target.toFixed(precision);
    }

    return target;
};

export const calculateWeight = (
    kg = 0,
    units: UnitTypes,
    precision = 0,
    withUnits = true
): string => {
    if (units === UnitTypes.Imperial) {
        return `${kgToLbs(kg).toFixed(precision)}${withUnits ? " lbs" : ""}`;
    }

    return `${kg.toFixed(precision)}${withUnits ? " kg" : ""}`;
};

export const getWeightDiff = (currentWeight: number, targetWeight: number, units: UnitTypes) => {
    const result = currentWeight - targetWeight;

    if (result < 0) {
        return `gain ${calculateWeight(Math.abs(result), units)}`;
    } else {
        return `lose ${calculateWeight(result, units)}`;
    }
};

export const validateWeight = (value: number, units: UnitTypes): string | null => {
    if (units === UnitTypes.Imperial) {
        if (value < MIN_WEIGHT_LBS || value > MAX_WEIGHT_LBS) {
            return i18n.t("please_enter_value_between_error", {
                min: MIN_WEIGHT_LBS,
                max: MAX_WEIGHT_LBS,
            });
        }
    } else {
        if (value < MIN_WEIGHT_KG || value > MAX_WEIGHT_KG) {
            return i18n.t("please_enter_value_between_error", {
                min: calculateWeight(MIN_WEIGHT_KG, units),
                max: calculateWeight(MAX_WEIGHT_KG, units),
            });
        }
    }

    return null;
};

export const getMaxMinWeights = (units: UnitTypes) =>
    units === UnitTypes.Imperial
        ? {
              min: `${MIN_WEIGHT_LBS} lbs`,
              max: `${MAX_WEIGHT_LBS} lbs`,
          }
        : {
              min: calculateWeight(MIN_WEIGHT_KG, units),
              max: calculateWeight(MAX_WEIGHT_KG, units),
          };
